import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { log } from '@/lib/logger';
import type { Rol } from '@/types/domain';

/**
 * AuthContext — única fuente de verdad del usuario logueado.
 * P-AUTH-03 (sesión unificada). P-AUTH-02 (default defensivo a rol mínimo).
 */

export interface Profile {
  id: string;
  role: Rol;
  empresa_id: string | null;
  administracion_id: string | null;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  activo: boolean;
}

interface AuthCtx {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  rol: Rol | null;
  isApex: boolean;
  isPartner: boolean;
  isPulse: boolean;
  isAdministrador: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Escuchar cambios de sesión
  useEffect(() => {
    let mounted = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        setSession(data.session);
      })
      .catch((e) => {
        log('error', 'auth.getSession', String(e));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      if (mounted) setSession(s);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Fetch del profile cuando hay sesión
  useEffect(() => {
    if (!session) {
      setProfile(null);
      return;
    }
    fetchProfile(session.user.id).then(setProfile);
  }, [session]);

  const rol: Rol | null = profile?.role ?? null;

  const value = useMemo<AuthCtx>(
    () => ({
      user: session?.user ?? null,
      session,
      profile,
      rol,
      isApex: rol === 'apex',
      isPartner: rol === 'partner',
      isPulse: rol === 'pulse',
      isAdministrador: rol === 'administrador',
      loading,
      signOut: async () => {
        await supabase.auth.signOut();
      },
      refetchProfile: async () => {
        if (!session) return;
        setProfile(await fetchProfile(session.user.id));
      },
    }),
    [session, profile, rol, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select(
      'id, role, empresa_id, administracion_id, full_name, avatar_url, phone, activo',
    )
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    log('error', 'auth.fetchProfile', error.message, { userId });
    return null;
  }
  if (!data) return null;
  // Default defensivo: si el rol de DB no matchea el union, caer a 'administrador' (mínimo)
  const validRoles: Rol[] = ['apex', 'partner', 'pulse', 'administrador'];
  const safeRole: Rol = validRoles.includes(data.role as Rol)
    ? (data.role as Rol)
    : 'administrador';
  return {
    id: data.id,
    role: safeRole,
    empresa_id: data.empresa_id,
    administracion_id: data.administracion_id,
    full_name: data.full_name,
    avatar_url: data.avatar_url,
    phone: (data as { phone?: string | null }).phone ?? null,
    activo: data.activo,
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fuera de AuthProvider');
  return v;
}
